/**
 * UpdatePrompt — surfaces a banner to PWA users when a new version of the
 * app has finished installing in the background and is waiting to take
 * over. The user taps "Reload" and we:
 *
 *   1. postMessage SKIP_WAITING to the waiting service worker so it
 *      activates immediately (without that, it would sit waiting until
 *      every tab is closed — a UX dead-end).
 *   2. Reload once the new SW takes control (`controllerchange` event),
 *      so the open tab boots into the freshest JS/CSS bundle.
 *
 * Receives the waiting registration via props; App.js owns the SW lifecycle
 * detection and passes it down. That keeps this component pure-UI and
 * trivially testable.
 *
 * Distinct from `InstallPwaPrompt` — that one nudges *not-yet-installed*
 * users to install. This one tells *already-installed* users to refresh
 * for the latest code.
 */
import React, { useState } from 'react';
import { RefreshCw, X } from 'lucide-react';
import { Button } from './ui/button';

export const UpdatePrompt = ({ waitingWorker, onClose }) => {
  const [reloading, setReloading] = useState(false);

  if (!waitingWorker) return null;

  const handleReload = () => {
    if (reloading) return;
    setReloading(true);

    // Listen for the new SW taking control, then hard-reload to pick up
    // the freshly-cached HTML/JS. We attach this BEFORE postMessage so we
    // don't race the activation.
    const onControllerChange = () => {
      window.location.reload();
    };
    if (navigator.serviceWorker) {
      navigator.serviceWorker.addEventListener('controllerchange', onControllerChange, { once: true });
    }

    try {
      waitingWorker.postMessage({ type: 'SKIP_WAITING' });
    } catch {
      // Fallback: if postMessage somehow fails, force-reload after a
      // short delay so the user isn't stuck on a half-updated app.
      setTimeout(() => window.location.reload(), 600);
    }
  };

  return (
    <div
      className="fixed inset-x-0 bottom-0 z-[85] flex justify-center px-3 pb-3 sm:pb-5 pointer-events-none"
      data-testid="update-prompt-root"
    >
      <div
        className="pointer-events-auto w-full max-w-md rounded-xl border border-cyan-400/25 bg-gradient-to-br from-[#0a2233] via-[#08182a] to-[#040d1a] shadow-2xl shadow-cyan-900/30 backdrop-blur-xl p-3.5 sm:p-4 flex items-start gap-3"
        data-testid="update-prompt"
        role="dialog"
        aria-label="App update available"
      >
        <div className="shrink-0 w-9 h-9 rounded-lg bg-cyan-500/15 border border-cyan-400/30 flex items-center justify-center">
          <RefreshCw className={`w-4 h-4 text-cyan-300 ${reloading ? 'animate-spin' : ''}`} />
        </div>
        <div className="flex-1 min-w-0 leading-snug">
          <p className="text-white text-sm font-medium">
            {reloading ? 'Reloading…' : 'New version available'}
          </p>
          <p className="text-white/55 text-[12px] mt-0.5">
            {reloading
              ? 'Refreshing to apply the latest update.'
              : 'Reload to apply the latest improvements. Your session resumes after.'}
          </p>
          <Button
            type="button"
            size="sm"
            onClick={handleReload}
            disabled={reloading}
            data-testid="update-prompt-reload"
            className="mt-2 h-7 px-3 bg-cyan-500 hover:bg-cyan-400 text-white text-xs gap-1.5 disabled:opacity-60"
          >
            <RefreshCw className="w-3.5 h-3.5" />
            {reloading ? 'Reloading…' : 'Reload now'}
          </Button>
        </div>
        <button
          type="button"
          onClick={onClose}
          disabled={reloading}
          data-testid="update-prompt-dismiss"
          className="shrink-0 text-white/35 hover:text-white/80 -m-1 p-1 transition-colors disabled:opacity-50"
          aria-label="Dismiss"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

export default UpdatePrompt;
